import { ref, type Ref } from "vue";

import type { Controller } from "@ctrl/controller-rpc";

import type { UseProfile } from "@/composables/useProfile";
import { getSelectedMeetingTranscriptionLanguage } from "@/recording/languages";
import { microphone } from "@/utils/microphone";

/**
 * Silence Deepgram waits before locking a voice-command phrase. Shorter than
 * meeting recording (500ms): the agent needs the utterance soon after the
 * user stops talking. Live text still updates via `interim_results`.
 */
const VOICE_INPUT_ENDPOINTING_MS = 300;

export type VoiceInputStatus = "idle" | "recording";

/**
 * Short mic-only streaming transcription via Deepgram (browser MediaRecorder).
 * Unlike meeting recording in `recording/core.ts`, this uses microphone only and
 * works on web as well as Electron.
 */
export function useVoiceInput(
  controller: Controller,
  /** For the account-scoped preferences (microphone, language). */
  profile: Ref<UseProfile>
) {
  const status = ref<VoiceInputStatus>("idle");
  const transcript = ref("");
  const partialText = ref("");
  const errorMessage = ref<string | null>(null);
  const mediaStream = ref<MediaStream | null>(null);

  let mediaRecorder: MediaRecorder | null = null;
  let stream: MediaStream | null = null;
  let ws: WebSocket | null = null;
  let keepAliveTimer: number | null = null;
  let websocketClosedByClient = false;

  const finalChunks: string[] = [];

  // Voice metrics are emitted from each frontend because mobile streams audio
  // straight to Deepgram without going through the controller, so there is no
  // shared server choke point. See the AI metrics plan.
  function pushVoiceMetric(
    action: "session_started" | "session_confirmed" | "session_cancelled"
  ) {
    void controller.metric.send(
      { object: "ai_voice", properties: { action } },
      {}
    );
  }

  function cleanupTracksAndRecorder() {
    try {
      mediaRecorder?.stop();
    } catch {
      // ignore
    }
    mediaRecorder = null;

    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      stream = null;
      mediaStream.value = null;
    }

    if (keepAliveTimer) {
      window.clearInterval(keepAliveTimer);
      keepAliveTimer = null;
    }
  }

  function closeWebSocket(sendCloseStream: boolean) {
    if (ws) {
      try {
        if (sendCloseStream && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "CloseStream" }));
        }
      } catch {
        // ignore
      }
      websocketClosedByClient = true;
      try {
        ws.close();
      } catch {
        // ignore
      }
    }
    ws = null;
  }

  function resetSessionState() {
    finalChunks.length = 0;
    partialText.value = "";
    transcript.value = "";
    errorMessage.value = null;
    websocketClosedByClient = false;
  }

  function buildTranscript(): string {
    const finals = finalChunks.map((s) => s.trim()).filter(Boolean);
    const partial = partialText.value.trim();
    const base = finals.join(" ").trim();
    if (!partial) return base;
    if (!base) return partial;
    return `${base} ${partial}`;
  }

  function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  function waitForWebSocketClose(socket: WebSocket, ms: number): Promise<void> {
    return new Promise((resolve) => {
      const done = () => resolve();
      const timer = window.setTimeout(done, ms);
      socket.addEventListener(
        "close",
        () => {
          window.clearTimeout(timer);
          done();
        },
        { once: true }
      );
    });
  }

  function pickRecorderMime(): string | undefined {
    const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
    for (const type of candidates) {
      if (MediaRecorder.isTypeSupported(type)) return type;
    }
    return undefined;
  }

  async function start(): Promise<void> {
    if (status.value === "recording") return;

    resetSessionState();
    microphone.getMedia();

    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      errorMessage.value =
        "Microphone access is not available in this browser.";
      return;
    }

    let micStream: MediaStream;
    try {
      const selectedId = microphone.getSelectedDeviceId(
        profile.value.user.accountId
      );
      micStream = await navigator.mediaDevices.getUserMedia(
        selectedId
          ? { audio: { deviceId: { ideal: selectedId } } }
          : { audio: true }
      );
    } catch (err) {
      if (
        err instanceof DOMException &&
        (err.name === "NotAllowedError" || err.name === "PermissionDeniedError")
      ) {
        errorMessage.value =
          "Microphone permission denied. Allow microphone access to use voice input.";
      } else {
        errorMessage.value =
          err instanceof Error ? err.message : "Could not access microphone.";
      }
      return;
    }

    stream = micStream;
    mediaStream.value = micStream;

    const accessToken = await controller.deepgramAccessToken();
    if (!accessToken) {
      cleanupTracksAndRecorder();
      errorMessage.value = "Speech recognition is temporarily unavailable.";
      return;
    }

    const listenUrl = new URL("wss://api.deepgram.com/v1/listen");
    listenUrl.searchParams.set("model", "nova-3");
    listenUrl.searchParams.set("smart_format", "true");
    listenUrl.searchParams.set(
      "language",
      getSelectedMeetingTranscriptionLanguage(profile.value.user.accountId)
    );
    listenUrl.searchParams.set(
      "endpointing",
      String(VOICE_INPUT_ENDPOINTING_MS)
    );
    listenUrl.searchParams.set("interim_results", "true");

    const socket = new WebSocket(listenUrl.toString(), ["bearer", accessToken]);
    ws = socket;
    const currentWs = socket;

    socket.onopen = () => {
      if (ws !== currentWs || websocketClosedByClient) return;

      const mimeType = pickRecorderMime();
      try {
        mediaRecorder = mimeType
          ? new MediaRecorder(stream!, { mimeType })
          : new MediaRecorder(stream!);
      } catch {
        cleanupTracksAndRecorder();
        closeWebSocket(false);
        errorMessage.value = "Could not start audio recording.";
        status.value = "idle";
        return;
      }

      if (currentWs.readyState === WebSocket.OPEN) {
        try {
          currentWs.send(JSON.stringify({ type: "KeepAlive" }));
        } catch {
          // ignore
        }
      }

      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (
          event.data.size > 0 &&
          currentWs.readyState === WebSocket.OPEN &&
          !websocketClosedByClient
        ) {
          event.data.arrayBuffer().then((buffer) => {
            try {
              if (currentWs.readyState === WebSocket.OPEN) {
                currentWs.send(buffer);
              }
            } catch {
              // ignore
            }
          });
        }
      };

      mediaRecorder.start(250);
      pushVoiceMetric("session_started");

      keepAliveTimer = window.setInterval(() => {
        if (
          !websocketClosedByClient &&
          currentWs.readyState === WebSocket.OPEN
        ) {
          try {
            currentWs.send(JSON.stringify({ type: "KeepAlive" }));
          } catch {
            // ignore
          }
        }
      }, 4000);

      status.value = "recording";
    };

    socket.onmessage = (event: MessageEvent) => {
      if (ws !== currentWs) return;
      try {
        if (typeof event.data !== "string") {
          throw new Error("Unexpected transcription message");
        }
        const data = JSON.parse(event.data);

        if (data?.type === "error") {
          errorMessage.value =
            typeof data.message === "string"
              ? data.message
              : "Speech recognition error.";
          status.value = "idle";
          cleanupTracksAndRecorder();
          closeWebSocket(false);
          return;
        }

        const alt = data?.channel?.alternatives?.[0];
        const isFinal = data?.is_final === true || data?.speech_final === true;
        if (!alt) return;

        const text: string = alt.transcript || "";

        if (!isFinal) {
          partialText.value = text;
          transcript.value = buildTranscript();
          return;
        }

        partialText.value = "";
        const trimmed = text.trim();
        if (trimmed) finalChunks.push(trimmed);
        transcript.value = buildTranscript();
      } catch {
        errorMessage.value = "Could not process transcription.";
      }
    };

    socket.onerror = () => {
      if (ws !== currentWs) return;
      errorMessage.value =
        "Unable to connect to speech recognition. Check your network and try again.";
      status.value = "idle";
      cleanupTracksAndRecorder();
      closeWebSocket(false);
    };

    socket.onclose = () => {
      if (ws !== currentWs) return;
      ws = null;
      if (!websocketClosedByClient && status.value === "recording") {
        errorMessage.value =
          errorMessage.value ??
          "Speech recognition connection closed unexpectedly.";
        status.value = "idle";
        cleanupTracksAndRecorder();
      }
    };
  }

  async function stop(): Promise<string> {
    if (status.value !== "recording") {
      return "";
    }

    const activeWs = ws;

    cleanupTracksAndRecorder();

    await sleep(150);

    if (activeWs?.readyState === WebSocket.OPEN) {
      try {
        activeWs.send(JSON.stringify({ type: "KeepAlive" }));
      } catch {
        // ignore
      }
      try {
        activeWs.send(JSON.stringify({ type: "CloseStream" }));
      } catch {
        // ignore
      }
      websocketClosedByClient = true;
      await waitForWebSocketClose(activeWs, 4000);
    } else {
      websocketClosedByClient = true;
    }

    try {
      activeWs?.close();
    } catch {
      // ignore
    }
    if (ws === activeWs) ws = null;

    const result = buildTranscript();
    partialText.value = "";
    transcript.value = result;
    status.value = "idle";
    pushVoiceMetric("session_confirmed");
    return result;
  }

  function cancel(): void {
    if (status.value !== "recording") return;
    status.value = "idle";
    pushVoiceMetric("session_cancelled");
    cleanupTracksAndRecorder();
    closeWebSocket(true);
    resetSessionState();
  }

  function dispose(): void {
    cancel();
  }

  return {
    status,
    transcript,
    partialText,
    errorMessage,
    mediaStream,
    start,
    stop,
    cancel,
    dispose,
  };
}

export type UseVoiceInput = ReturnType<typeof useVoiceInput>;
